import { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { AdminMetrics } from "@/components/admin/AdminMetrics";
import { UserActivityTable } from "@/components/admin/UserActivityTable";
import { useToast } from "@/components/ui/use-toast";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { UsersManagement } from "@/components/admin/UsersManagement";

const AdminDashboard = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [isAuthorized, setIsAuthorized] = useState(false);

  useEffect(() => {
    const checkAuthorization = async () => {
      const { data: { session } } = await supabase.auth.getSession();
      
      if (!session || session.user.email !== 'ahmed@supplyfied.com') {
        toast({
          variant: "destructive",
          title: "Unauthorized Access",
          description: "You don't have permission to view this page."
        });
        navigate('/');
        return;
      }
      
      setIsAuthorized(true);
    };

    checkAuthorization();
  }, [navigate]);

  if (!isAuthorized) {
    return null;
  }

  return (
    <div className="max-w-7xl mx-auto space-y-8">
      <div className="bg-[#0A0F29] text-white p-8 rounded-lg">
        <h1 className="text-3xl font-bold mb-2 text-[#00BFA6]">
          Admin Dashboard
        </h1>
        <p className="text-gray-300">
          Manage content and monitor user activity in the data room
        </p>
      </div>

      <Tabs defaultValue="metrics" className="space-y-6">
        <TabsList className="bg-white">
          <TabsTrigger value="metrics">Analytics</TabsTrigger>
          <TabsTrigger value="content">Content Management</TabsTrigger>
          <TabsTrigger value="users">Users</TabsTrigger>
          <TabsTrigger value="activity">User Activity</TabsTrigger>
        </TabsList>

        <TabsContent value="metrics">
          <AdminMetrics />
        </TabsContent>

        <TabsContent value="content">
          <Card>
            <CardHeader>
              <CardTitle>Content Management</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                <Link to="/admin/edit/business-summary">
                  <Button variant="outline" className="w-full">Business Summary</Button>
                </Link>
                <Link to="/admin/edit/team">
                  <Button variant="outline" className="w-full">Team</Button>
                </Link>
                <Link to="/admin/edit/product">
                  <Button variant="outline" className="w-full">Product</Button>
                </Link>
                <Link to="/admin/edit/market">
                  <Button variant="outline" className="w-full">Market</Button>
                </Link>
                <Link to="/admin/edit/legal">
                  <Button variant="outline" className="w-full">Legal</Button>
                </Link>
                <Link to="/admin/edit/risks">
                  <Button variant="outline" className="w-full">Risks</Button>
                </Link>
                <Link to="/admin/edit/funds">
                  <Button variant="outline" className="w-full">Use of Funds</Button>
                </Link>
                <Link to="/admin/edit/financials">
                  <Button variant="outline" className="w-full">Financials</Button>
                </Link>
                <Link to="/admin/edit/traction">
                  <Button variant="outline" className="w-full">Traction</Button>
                </Link>
                <Link to="/admin/edit/cap-table">
                  <Button variant="outline" className="w-full">Cap Table</Button>
                </Link>
                <Link to="/admin/edit/downloadable-files">
                  <Button variant="outline" className="w-full">Downloadable Files</Button>
                </Link>
                <Link to="/admin/edit/archived-files">
                  <Button variant="outline" className="w-full">Archived Newsletter / Pitch Decks</Button>
                </Link>
                <Link to="/admin/edit/email-templates">
                  <Button variant="outline" className="w-full">Email Templates</Button>
                </Link>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="users">
          <UsersManagement />
        </TabsContent>

        <TabsContent value="activity">
          <UserActivityTable />
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default AdminDashboard;
